import { findResultsState } from 'react-instantsearch-dom/server';
import { withRouter } from 'next/router';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React from 'react';

import App from '../../pages/_app';
import Algolia from './algolia';
import searchClient from '../../utils/algolia';
import * as helpers from './utils/helpers';
import {
  shopGSSP,
  trendsGSSP,
  brandsGSSP,
  lpcGSSP,
  editsGSSP
} from './gssp';

const updateAfter = 700;

const DEFAULT_PROPS = {
  searchClient,
  indexName: process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX
};

class Page extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchState: this.props.searchState,
      // eslint-disable-next-line react/no-unused-state
      lastRouter: this.props.router
    };
  }

  static getDerivedStateFromProps = (props, state) => {
    if (!isEqual(state.lastRouter, props.router)) {
      return {
        searchState: helpers.pathToSearchState(props.router.asPath),
        lastRouter: props.router
      };
    }

    return null;
  }

  searchStateToURL = (searchState) => (searchState ? helpers.createURL(searchState, this.props) : '')

  onSearchStateChange = (searchState) => {
    clearTimeout(this.debouncedSetState);

    this.debouncedSetState = setTimeout(() => {
      const href = this.searchStateToURL(searchState);
      this.props.router.push(href, href, { shallow: true });
    }, updateAfter);

    this.setState({ searchState }, () => {
      // scroll to top after state change
      if (searchState.page === 1) global.window.scrollTo(0, 0);
    });
  };

  render() {
    return (
      <Algolia
        {...DEFAULT_PROPS}
        searchState={this.state.searchState}
        resultsState={this.props.resultsState}
        onSearchStateChange={this.onSearchStateChange}
        createURL={(state) => helpers.createURL(state, this.props)}
        brand={this.props.brand}
        lpc={this.props.lpc}
        trend={this.props.trend}
        edit={this.props.edit}
      />
    );
  }
}

/**
 *
 * “findResultsState” accepts the FULL APPLICATION as
 * the first argument (this hack allows us to load the application on
 * the server-side without any issues from the client or server-specific
 * libraries).
 *
 */
const Maisonette = (props) => <App Component={Algolia} pageProps={props} />;

/**
 *
 * The SSR portion of these pages is contained to the algoliaGSSP
 * method which exists in organisms/algolia-plp.
 * This method uses “findResultsState” to render the application
 * on the server-side and get the searchState / searchResults to pass
 * to the client.
 *
 */
export const algoliaGSSP = async ({ resolvedUrl, ...context }, page) => {
  const searchState = helpers.pathToSearchState(resolvedUrl);
  const resultsState = await findResultsState(Maisonette, {
    ...DEFAULT_PROPS,
    searchState
  });

  let props;

  const resultsRedirect = resultsState?.rawResults?.[0]?.userData?.[0]?.redirect;
  if (page === 'shop') props = shopGSSP(context);
  if (page === 'trends') props = trendsGSSP(context);
  if (page === 'brands') {
    props = await brandsGSSP(context);
    if (props.noResults) {
      return {
        redirect: {
          destination: '/brands',
          permanent: false
        }
      };
    }
  }
  if (page === 'lpc') props = lpcGSSP(context);
  if (page === 'edits') props = editsGSSP(context);

  if (resultsRedirect && context?.req?.url !== resultsRedirect) {
    return {
      redirect: {
        destination: resultsState.rawResults[0].userData[0].redirect,
        permanent: false
      }
    };
  }
  return {
    props: {
      ...props,
      // next.js is great, this is great
      // https://github.com/vercel/next.js/issues/11993#issuecomment-617375501
      resultsState: JSON.parse(JSON.stringify(resultsState)),
      searchState
    }
  };
};

Page.defaultProps = {
  brand: null,
  edit: null,
  lpc: null,
  trend: null
};

Page.propTypes = {
  brand: PropTypes.object,
  edit: PropTypes.string,
  lpc: PropTypes.string,
  resultsState: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  searchState: PropTypes.object.isRequired,
  trend: PropTypes.string
};

export default withRouter(Page);
